import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Custom hook for debounced search functionality
 * Provides immediate UI updates with debounced filtering/API calls
 *
 * @param {string} initialValue - Initial search value
 * @param {number} delay - Debounce delay in milliseconds (default: 300)
 * @returns {Array} [searchValue, debouncedValue, setSearchValue, clearSearch]
 */
export const useDebouncedSearch = (initialValue = "", delay = 300) => {
  const [searchValue, setSearchValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);
  const timeoutRef = useRef(null);

  // Debounce the search value
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(searchValue);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchValue, delay]);

  // Set search value (immediate UI update)
  const setSearch = useCallback((value) => {
    setSearchValue(value);
  }, []);

  // Clear search value
  const clearSearch = useCallback(() => {
    setSearchValue("");
    setDebouncedValue("");
  }, []);

  // Update search value from external source (e.g., URL params)
  const updateSearch = useCallback((value) => {
    setSearchValue(value);
    setDebouncedValue(value);
  }, []);

  return [searchValue, debouncedValue, setSearch, clearSearch, updateSearch];
};

export default useDebouncedSearch;
